<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, prettier/prettier -->
<script setup>
/* eslint-disable */
import { computed, inject, proxyRefs, ref, watch } from 'vue';
import { useImudeiRuntimeModel } from './useImudeiRuntimeModel';

const props = defineProps({
  preview: { type: Boolean, default: false },
  records: { type: Array, default: () => [] },
  total: { type: Number, default: 0 },
});

const emit = defineEmits(['navigate']);
const realData = inject('imudeiRealData');
const { error, loading, refresh } = realData;
const model = proxyRefs(
  useImudeiRuntimeModel({
    realData,
    module: computed(() => 'brokers'),
    screen: computed(() => 'dashboard'),
  })
);

const searchQuery = ref('');
const selectedId = ref('');

const asArray = value => (Array.isArray(value) ? value : []);
const sourceOf = record => record?.source || record || {};
const valueAt = (record, path) =>
  path.split('.').reduce((value, key) => value?.[key], record);
const firstValue = (record, ...paths) => {
  const value = paths
    .map(path => valueAt(record, path))
    .find(item => item !== undefined && item !== null && item !== '');

  return value ?? '';
};
const initials = value =>
  String(value || '—')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0])
    .join('')
    .toLocaleUpperCase('pt-BR');
const imageUrlFrom = value => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return imageUrlFrom(value[0]);

  return imageUrlFrom(
    firstValue(value, 'url', 'src', 'image_url', 'file_url', 'preview_url')
  );
};
const avatarFor = source =>
  imageUrlFrom(
    firstValue(
      source,
      'avatar_url',
      'thumbnail_url',
      'photo_url',
      'user.avatar_url',
      'user.thumbnail_url',
      'user.photo_url',
      'image'
    )
  );
const availabilityFor = source => {
  if (source?.public === true) return 'Perfil público';
  if (source?.public === false) return 'Perfil privado';

  return (
    firstValue(
      source,
      'availability_label',
      'availability',
      'status_label',
      'status'
    ) || 'Status não retornado'
  );
};
const availabilityTone = value => {
  const status = String(value || '').toLocaleLowerCase('pt-BR');
  if (/públic|public|ativo|dispon/.test(status)) {
    return 'bg-emerald-50 text-emerald-700';
  }
  if (/privad|pend|aguard/.test(status)) return 'bg-amber-50 text-amber-700';
  return 'bg-slate-50 text-slate-600';
};

const recordInput = computed(() =>
  props.records.length ? props.records : asArray(model.records)
);
const agencyProfile = computed(() => realData.data.value?.profile || {});
const agencyName = computed(
  () =>
    firstValue(agencyProfile.value, 'nome_fantasia', 'razao_social', 'name') ||
    'Imobiliária não retornada'
);
const brokerRows = computed(() =>
  recordInput.value
    .map(record => {
      const source = sourceOf(record);
      const id = String(record.id || source.id || source.user_id || '');
      if (!id) return null;

      return {
        activeListings:
          firstValue(source, 'active_listings', 'listings_count') || '—',
        agentCode: firstValue(source, 'agent_code', 'creci', 'slug'),
        avatarUrl: avatarFor(source),
        id,
        name:
          record.title ||
          firstValue(source, 'name', 'display_name', 'user.name', 'email') ||
          'Nome não retornado',
        role:
          record.subtitle ||
          firstValue(source, 'role_label', 'role', 'profile', 'position') ||
          'Função não retornada',
        source,
        status: availabilityFor(source),
      };
    })
    .filter(Boolean)
);
const filteredBrokers = computed(() => {
  const query = searchQuery.value.trim().toLocaleLowerCase('pt-BR');
  if (!query) return brokerRows.value;

  return brokerRows.value.filter(broker =>
    `${broker.name} ${broker.role} ${broker.agentCode} ${broker.status}`
      .toLocaleLowerCase('pt-BR')
      .includes(query)
  );
});
const visibleBrokers = computed(() => filteredBrokers.value.slice(0, 8));
const highlightBrokers = computed(() => brokerRows.value.slice(0, 6));
const activityEntries = computed(() =>
  asArray(model.history)
    .slice(-3)
    .reverse()
    .map(activity => ({
      actor:
        firstValue(activity, 'actor', 'author', 'user.name') || 'Atividade',
      date: firstValue(activity, 'date', 'created_at', 'updated_at'),
      id: activity.id || `${activity.label}-${activity.date}`,
      label:
        firstValue(activity, 'label', 'title', 'description') ||
        'Atualização retornada pela API.',
    }))
);
const selected = computed(
  () => brokerRows.value.find(broker => broker.id === selectedId.value) || null
);
const totalBrokers = computed(() => props.total || brokerRows.value.length);
const listUnavailableMessage = computed(() => {
  if (loading.value) return 'Carregando corretores reais…';
  if (error.value) return error.value;
  if (searchQuery.value.trim())
    return 'Nenhum corretor real corresponde à busca.';
  if (props.preview)
    return 'A visualização de referência não possui dados carregados.';
  return 'A API não retornou corretores para esta conta.';
});

const selectBroker = broker => {
  if (broker?.id) selectedId.value = broker.id;
};
const closeQuickView = () => {
  selectedId.value = '';
};
const openProfile = () => {
  if (selected.value?.id) {
    emit('navigate', { id: selected.value.id, screen: 'detail' });
  }
};

watch(
  brokerRows,
  rows => {
    if (!rows.some(broker => broker.id === selectedId.value)) {
      selectedId.value = rows[0]?.id || '';
    }
  },
  { immediate: true }
);
</script>

<template>
  <main
    class="min-h-full w-full pb-8 pl-[116px] pr-[526px] pt-8 text-[#101958]"
  >
    <header
      data-imudei-visual-id="broker-dashboard-header"
      class="flex items-start justify-between gap-5"
    >
      <div>
        <h1 class="text-[25px] font-bold tracking-[-0.035em]">
          Corretor social <span aria-hidden="true">👋</span>
        </h1>
        <p class="mt-1 text-[9px] text-[#7180a5]">
          Conecte e acompanhe os dados reais retornados para o seu time.
        </p>
      </div>

      <div class="flex items-center gap-2">
        <label
          class="flex h-9 w-[245px] items-center gap-2 rounded-xl border border-[#e8e4ef] bg-white px-3 text-[#7b86a4] shadow-[0_4px_16px_rgba(53,34,100,0.03)]"
        >
          <i class="i-lucide-search size-3.5" />
          <input
            v-model="searchQuery"
            class="min-w-0 flex-1 border-0 bg-transparent p-0 text-[8px] text-[#101958] outline-none placeholder:text-[#9aa4bb]"
            placeholder="Buscar corretor por nome, CRECI ou loja"
            type="search"
          />
        </label>
        <button
          class="flex h-9 items-center gap-2 rounded-xl border border-[#e8e4ef] bg-white px-3 text-[8px] font-semibold text-[#566385] disabled:cursor-not-allowed disabled:opacity-60"
          disabled
          title="Filtro de lojas não retornado pela API"
          type="button"
        >
          Todas as lojas
          <i class="i-lucide-chevron-down size-3" />
        </button>
        <button
          class="flex h-9 items-center gap-2 rounded-xl border border-[#e8e4ef] bg-white px-3 text-[8px] font-semibold text-[#566385] disabled:cursor-not-allowed disabled:opacity-60"
          disabled
          title="Filtros avançados não retornados pela API"
          type="button"
        >
          <i class="i-lucide-sliders-horizontal size-3" />
          Mais filtros
        </button>
        <button
          class="grid size-9 place-items-center rounded-xl border border-[#e8e4ef] bg-white text-[#5a24c7] disabled:cursor-not-allowed disabled:opacity-60"
          disabled
          title="Visualizações alternativas não retornadas pela API"
          type="button"
        >
          <i class="i-lucide-layout-grid size-3.5" />
        </button>
        <button
          class="grid size-9 place-items-center rounded-xl border border-[#e8e4ef] bg-white text-[#5a24c7] shadow-[0_4px_16px_rgba(53,34,100,0.03)]"
          :disabled="loading"
          title="Atualizar dados reais"
          type="button"
          @click="refresh"
        >
          <i
            class="i-lucide-refresh-cw size-3.5"
            :class="loading ? 'animate-spin' : ''"
          />
        </button>
      </div>
    </header>

    <section class="mt-1 grid grid-cols-[minmax(0,1fr)_190px] gap-4">
      <article
        data-imudei-visual-id="broker-dashboard-highlights"
        class="h-[220px] rounded-2xl border border-[#e8e4ef] bg-white p-5 shadow-[0_4px_18px_rgba(53,34,100,0.03)]"
      >
        <header class="flex items-center justify-between">
          <div>
            <h2 class="text-[11px] font-bold">Destaques do mês</h2>
            <p class="mt-1 text-[7px] text-[#7480a0]">
              Perfis reais retornados pela API da sua conta.
            </p>
          </div>
          <span
            class="rounded-full bg-violet-50 px-2.5 py-1 text-[7px] font-semibold text-[#5a24c7]"
          >
            {{ brokerRows.length }} carregados
          </span>
        </header>

        <div v-if="highlightBrokers.length" class="mt-5 grid grid-cols-6 gap-3">
          <button
            v-for="broker in highlightBrokers"
            :key="broker.id"
            class="min-w-0 text-center"
            type="button"
            @click="selectBroker(broker)"
          >
            <i
              class="mx-auto grid size-[62px] place-items-center rounded-full border-[3px] bg-gradient-to-br from-[#f9f5ff] to-[#e9f8ff] text-[12px] font-bold"
              :class="
                selected?.id === broker.id
                  ? 'border-[#6a2ddb] text-[#5a24c7]'
                  : 'border-[#d7c8f8] text-[#7b5bc9]'
              "
            >
              {{ initials(broker.name) }}
            </i>
            <strong class="mt-2 block truncate text-[8px]">{{
              broker.name
            }}</strong>
            <small class="mt-1 block truncate text-[7px] text-[#7480a0]">
              {{
                broker.activeListings === '—'
                  ? 'Anúncios não retornados'
                  : `${broker.activeListings} anúncios`
              }}
            </small>
          </button>
        </div>

        <div
          v-else
          class="grid h-[142px] place-items-center text-center text-[#8a94ad]"
        >
          <span>
            <i class="i-lucide-users-round mx-auto size-6" />
            <strong class="mt-2 block text-[9px]"
              >Sem destaques disponíveis</strong
            >
            <small class="mt-1 block text-[7px]"
              >A API não retornou perfis para exibir.</small
            >
          </span>
        </div>
      </article>

      <div class="grid h-[220px] grid-rows-[132px_76px] gap-3">
        <article
          class="rounded-2xl border border-[#e8e4ef] bg-white p-4 shadow-[0_4px_18px_rgba(53,34,100,0.03)]"
        >
          <header class="flex items-center justify-between">
            <h2 class="text-[10px] font-bold">Aniversariantes</h2>
            <i class="i-lucide-gift size-4 text-[#5a24c7]" />
          </header>
          <div class="mt-4 flex items-center gap-2 text-[#8a94ad]">
            <i
              class="grid size-8 place-items-center rounded-full border border-dashed border-[#dcd6e7] bg-[#faf9fc]"
            >
              <i class="i-lucide-calendar-x size-3.5" />
            </i>
            <span>
              <strong class="block text-[8px]">Indisponível</strong>
              <small class="mt-1 block text-[7px]"
                >Sem endpoint de aniversários</small
              >
            </span>
          </div>
        </article>

        <article
          class="rounded-2xl border border-[#e8e4ef] bg-white p-4 shadow-[0_4px_18px_rgba(53,34,100,0.03)]"
        >
          <header class="flex justify-between">
            <h2 class="text-[10px] font-bold">Presença</h2>
            <small class="text-[7px] text-[#7a85a2]">Sem endpoint</small>
          </header>
          <div class="mt-4 flex items-center gap-2 text-[#8a94ad]">
            <i class="grid size-8 place-items-center rounded-full bg-[#f6f4f9]">
              <i class="i-lucide-radio-tower size-3.5" />
            </i>
            <small class="text-[7px] leading-3"
              >A API não retorna presença online.</small
            >
          </div>
        </article>
      </div>
    </section>

    <section
      data-imudei-visual-id="broker-dashboard-table"
      class="mt-1 overflow-hidden rounded-2xl border border-[#e8e4ef] bg-white shadow-[0_4px_18px_rgba(53,34,100,0.03)]"
    >
      <header
        class="flex items-center justify-between border-b border-[#eeeaf4] px-4 py-3"
      >
        <span>
          <h2 class="text-[11px] font-bold">Corretores ({{ totalBrokers }})</h2>
          <p class="mt-1 text-[7px] text-[#7480a0]">
            Selecione uma linha para atualizar a visualização rápida.
          </p>
        </span>
        <span class="text-[7px] text-[#7480a0]">Dados operacionais reais</span>
      </header>

      <div
        class="grid grid-cols-[minmax(0,2.15fr)_0.65fr_0.7fr_0.72fr_0.8fr_0.82fr_38px] gap-2 border-b border-[#eeeaf4] bg-[#fcfbfe] px-4 py-2 text-[7px] font-semibold text-[#7784a6]"
      >
        <span>Corretor</span>
        <span>Anúncios</span>
        <span>Vendas</span>
        <span>Comissão</span>
        <span>Status</span>
        <span>Última atividade</span>
        <span aria-label="Visualizar" />
      </div>

      <div v-if="visibleBrokers.length" class="flex min-h-[342px] flex-col">
        <button
          v-for="broker in visibleBrokers"
          :key="broker.id"
          class="grid min-h-[56px] w-full grid-cols-[minmax(0,2.15fr)_0.65fr_0.7fr_0.72fr_0.8fr_0.82fr_38px] items-center gap-2 border-b border-[#f1eef5] px-4 py-2 text-left last:border-b-0 hover:bg-[#fcfbfe]"
          :class="selected?.id === broker.id ? 'bg-[#fbf9ff]' : 'bg-white'"
          type="button"
          @click="selectBroker(broker)"
        >
          <span class="flex min-w-0 items-center gap-3">
            <i
              class="grid size-9 shrink-0 place-items-center overflow-hidden rounded-full bg-gradient-to-br from-violet-100 to-sky-100 text-[8px] font-bold text-[#5a24c7]"
            >
              <img
                v-if="broker.avatarUrl"
                :alt="broker.name"
                :src="broker.avatarUrl"
                class="size-full object-cover"
              />
              <template v-else>{{ initials(broker.name) }}</template>
            </i>
            <span class="min-w-0">
              <strong class="block truncate text-[9px]">{{
                broker.name
              }}</strong>
              <small class="mt-1 block truncate text-[7px] text-[#7480a0]">
                {{ broker.role
                }}<span v-if="broker.agentCode"> · {{ broker.agentCode }}</span>
              </small>
            </span>
          </span>
          <span>
            <strong class="block text-[10px]">{{
              broker.activeListings
            }}</strong>
            <small class="mt-1 block text-[6px] text-[#7480a0]">ativos</small>
          </span>
          <span>
            <strong class="block text-[10px] text-[#8a94ad]">—</strong>
            <small class="mt-1 block text-[6px] text-[#7480a0]"
              >sem contrato</small
            >
          </span>
          <span>
            <strong class="block text-[10px] text-[#8a94ad]">—</strong>
            <small class="mt-1 block text-[6px] text-[#7480a0]"
              >sem contrato</small
            >
          </span>
          <span>
            <small
              class="inline-flex rounded-full px-2 py-1 text-[7px] font-semibold"
              :class="availabilityTone(broker.status)"
            >
              {{ broker.status }}
            </small>
          </span>
          <span class="text-[7px] text-[#7a85a2]">Sem atividade retornada</span>
          <span
            class="grid size-8 place-items-center rounded-xl border border-[#ece8f2] text-[#5a24c7]"
          >
            <i class="i-lucide-panel-right-open size-3.5" />
          </span>
        </button>
        <p
          v-if="visibleBrokers.length < 8"
          class="mt-auto border-t border-dashed border-[#ece8f2] px-4 py-3 text-center text-[7px] text-[#8490ad]"
        >
          A API retornou {{ visibleBrokers.length }} de
          {{ totalBrokers }} corretores para esta conta.
        </p>
      </div>

      <div
        v-else
        class="grid h-[310px] place-items-center text-center text-[#8a94ad]"
      >
        <span>
          <i class="i-lucide-users-round mx-auto size-7" />
          <strong class="mt-3 block text-[10px]"
            >Nenhum corretor disponível</strong
          >
          <small class="mt-1 block max-w-sm text-[8px]">{{
            listUnavailableMessage
          }}</small>
        </span>
      </div>

      <footer
        class="flex items-center justify-between border-t border-[#eeeaf4] px-4 py-3 text-[7px] text-[#7480a0]"
      >
        <span
          >Mostrando {{ visibleBrokers.length }} de
          {{ totalBrokers }} corretores</span
        >
        <span class="flex items-center gap-2">
          <button
            class="grid size-7 place-items-center rounded-lg border border-[#ece8f2] opacity-50"
            disabled
            type="button"
          >
            <i class="i-lucide-chevron-left size-3" />
          </button>
          <span
            class="grid size-7 place-items-center rounded-lg bg-[#5a24c7] font-bold text-white"
            >1</span
          >
          <button
            class="grid size-7 place-items-center rounded-lg border border-[#ece8f2] opacity-50"
            disabled
            type="button"
          >
            <i class="i-lucide-chevron-right size-3" />
          </button>
        </span>
      </footer>
    </section>

    <aside
      data-imudei-visual-id="broker-dashboard-quick-view"
      class="fixed inset-y-0 right-0 z-40 w-[510px] overflow-y-auto border-l border-[#ebe6f1] bg-[#fcfbfe] shadow-[-8px_0_28px_rgba(49,29,90,0.08)]"
    >
      <section v-if="selected" class="min-h-full pb-5">
        <article
          class="relative h-[164px] overflow-hidden bg-[linear-gradient(120deg,#e5f6ff_0%,#f7efff_58%,#f7dced_100%)]"
        >
          <span
            class="absolute inset-0 bg-[radial-gradient(circle_at_18%_34%,rgba(118,224,255,0.35),transparent_36%),radial-gradient(circle_at_82%_20%,rgba(212,165,255,0.35),transparent_32%)]"
          />
          <button
            class="absolute right-4 top-4 grid size-9 place-items-center rounded-full bg-white/80 text-[#3e2b6e] shadow-sm"
            title="Fechar visualização rápida"
            type="button"
            @click="closeQuickView"
          >
            <i class="i-lucide-x size-4" />
          </button>
          <div class="absolute left-7 top-7 flex items-start gap-4">
            <i
              class="grid size-[112px] place-items-center overflow-hidden rounded-full border-4 border-white bg-gradient-to-br from-[#ef5bbc] via-[#7643e4] to-[#52cbed] p-1 text-[28px] font-bold text-[#5924c7] shadow-lg"
            >
              <img
                v-if="selected.avatarUrl"
                :alt="selected.name"
                :src="selected.avatarUrl"
                class="size-full rounded-full object-cover"
              />
              <i
                v-else
                class="grid size-full place-items-center rounded-full bg-[#fbf8ff]"
                >{{ initials(selected.name) }}</i
              >
            </i>
            <span class="pt-6">
              <span class="flex items-center gap-2">
                <h2 class="max-w-[255px] truncate text-[22px] font-bold">
                  {{ selected.name }}
                </h2>
                <small
                  class="rounded-full bg-[#6b2cdc] px-2 py-1 text-[7px] font-bold text-white"
                  >Perfil real</small
                >
              </span>
              <small
                class="mt-1 block text-[9px] font-semibold text-[#433162]"
                >{{ selected.role }}</small
              >
              <small
                class="mt-2 flex items-center gap-2 text-[8px] text-[#596486]"
              >
                <span v-if="selected.agentCode" class="flex items-center gap-1"
                  ><i class="i-lucide-id-card size-3" />{{
                    selected.agentCode
                  }}</span
                >
                <span class="flex items-center gap-1"
                  ><i class="i-lucide-building-2 size-3" />{{
                    agencyName
                  }}</span
                >
              </small>
            </span>
          </div>
        </article>

        <div
          class="grid grid-cols-4 gap-2 border-b border-[#eeeaf4] bg-white px-4 py-3"
        >
          <button
            class="grid h-10 place-items-center gap-1 rounded-xl border border-[#ece8f2] text-[7px] font-semibold text-[#5a24c7]"
            type="button"
            @click="openProfile"
          >
            <i class="i-lucide-panel-right-open size-4" />
            Ver perfil
          </button>
          <button
            class="grid h-10 place-items-center gap-1 rounded-xl border border-[#ece8f2] text-[7px] font-semibold text-[#8a94ad]"
            disabled
            title="Canal não retornado"
            type="button"
          >
            <i class="i-lucide-message-circle-off size-4" />
            Mensagem
          </button>
          <button
            class="grid h-10 place-items-center gap-1 rounded-xl border border-[#ece8f2] text-[7px] font-semibold text-[#8a94ad]"
            disabled
            title="Canal não retornado"
            type="button"
          >
            <i class="i-lucide-mail-x size-4" />
            E-mail
          </button>
          <button
            class="grid h-10 place-items-center gap-1 rounded-xl border border-[#ece8f2] text-[7px] font-semibold text-[#8a94ad]"
            disabled
            title="Ações adicionais não retornadas"
            type="button"
          >
            <i class="i-lucide-more-horizontal size-4" />
            Mais
          </button>
        </div>

        <article
          class="mx-4 mt-4 rounded-2xl border border-[#efe2f5] bg-gradient-to-r from-[#faf0ff] via-white to-[#ffeaf8] p-4"
        >
          <div class="flex items-center gap-3">
            <i
              class="grid size-11 place-items-center rounded-xl bg-violet-100 text-[#7551c9]"
            >
              <i class="i-lucide-gem size-5" />
            </i>
            <span class="min-w-0 flex-1">
              <strong class="block text-[10px]"
                >Missões para ganhar pontos indisponíveis</strong
              >
              <small class="mt-1 block text-[8px] leading-4 text-[#69759c]"
                >O backend não retorna missões, pontos ou recompensas para este
                corretor.</small
              >
            </span>
          </div>
        </article>

        <article
          class="mx-4 mt-3 rounded-2xl border border-[#e8e4ef] bg-white p-4 shadow-[0_4px_18px_rgba(53,34,100,0.03)]"
        >
          <header class="flex justify-between">
            <h3 class="text-[10px] font-bold">Desempenho disponível</h3>
            <small class="text-[7px] text-[#7a85a2]">Perfil real</small>
          </header>
          <div
            class="mt-4 grid grid-cols-[1fr_1fr_1fr_76px] divide-x divide-[#eeeaf4] text-center"
          >
            <span>
              <small class="block text-[7px] text-[#7480a0]">Anúncios</small>
              <strong class="mt-2 block text-[14px]">{{
                selected.activeListings
              }}</strong>
              <small class="mt-1 block text-[6px] text-[#7a85a2]">Ativos</small>
            </span>
            <span>
              <small class="block text-[7px] text-[#7480a0]">Vendas</small>
              <strong class="mt-2 block text-[14px] text-[#8b95ae]">—</strong>
              <small class="mt-1 block text-[6px] text-[#7a85a2]"
                >Sem contrato</small
              >
            </span>
            <span>
              <small class="block text-[7px] text-[#7480a0]">Comissão</small>
              <strong class="mt-2 block text-[14px] text-[#8b95ae]">—</strong>
              <small class="mt-1 block text-[6px] text-[#7a85a2]"
                >Sem contrato</small
              >
            </span>
            <span class="grid place-items-center">
              <i
                class="grid size-[58px] place-items-center rounded-full border-[7px] border-[#eeeaf4] text-[8px] font-semibold text-[#8a94ad]"
                >—</i
              >
            </span>
          </div>
          <div class="mt-4 h-1.5 rounded-full bg-[#eeeaf3]" />
          <p class="mt-2 text-[7px] text-[#7a85a2]">
            Metas mensais não são retornadas pela API.
          </p>
        </article>

        <article
          class="mx-4 mt-3 rounded-2xl border border-[#e8e4ef] bg-white p-4 shadow-[0_4px_18px_rgba(53,34,100,0.03)]"
        >
          <header class="flex justify-between">
            <h3 class="text-[10px] font-bold">Portfólio em destaque</h3>
            <small class="text-[7px] text-[#7a85a2]">Sem endpoint</small>
          </header>
          <div class="mt-3 grid grid-cols-3 gap-2">
            <span
              v-for="index in 3"
              :key="index"
              class="overflow-hidden rounded-xl border border-[#eeeaf4] bg-white"
            >
              <span
                class="grid h-[56px] place-items-center bg-gradient-to-br from-[#bdc7da] via-[#ded4d9] to-[#efcdaa] text-white/90"
              >
                <i class="i-lucide-image-off size-4" />
              </span>
              <small class="block p-2 text-center text-[6px] text-[#7a85a2]"
                >Lista não retornada</small
              >
            </span>
          </div>
        </article>

        <article
          class="mx-4 mt-3 rounded-2xl border border-[#e8e4ef] bg-white p-4 shadow-[0_4px_18px_rgba(53,34,100,0.03)]"
        >
          <header class="flex justify-between">
            <h3 class="text-[10px] font-bold">Conquistas</h3>
            <small class="text-[7px] text-[#7a85a2]">Sem endpoint</small>
          </header>
          <div class="mt-4 grid grid-cols-4 gap-2 text-center">
            <span v-for="index in 4" :key="index">
              <i
                class="mx-auto grid size-10 place-items-center rounded-xl border border-dashed border-[#dcd6e7] bg-[#faf9fc] text-[#9aa4bb]"
              >
                <i class="i-lucide-badge-x size-4" />
              </i>
              <small class="mt-2 block text-[6px] text-[#7a85a2]"
                >Indisponível</small
              >
            </span>
          </div>
        </article>

        <article
          class="mx-4 mt-3 rounded-2xl border border-[#e8e4ef] bg-white p-4 shadow-[0_4px_18px_rgba(53,34,100,0.03)]"
        >
          <header class="flex justify-between">
            <h3 class="text-[10px] font-bold">Feed recente</h3>
            <small class="text-[7px] text-[#7a85a2]">Dados reais</small>
          </header>
          <div v-if="activityEntries.length" class="mt-4 space-y-3">
            <div
              v-for="activity in activityEntries"
              :key="activity.id"
              class="flex gap-3 border-b border-[#f0edf5] pb-3 last:border-b-0 last:pb-0"
            >
              <i
                class="grid size-8 shrink-0 place-items-center rounded-full bg-violet-50 text-[8px] font-bold text-[#5a24c7]"
                >{{ initials(activity.actor) }}</i
              >
              <span class="min-w-0 flex-1">
                <strong class="block truncate text-[8px]">{{
                  activity.actor
                }}</strong>
                <small class="mt-1 block text-[7px] leading-3 text-[#69759c]">{{
                  activity.label
                }}</small>
              </span>
              <small class="shrink-0 text-[7px] text-[#8490ad]">{{
                activity.date || '—'
              }}</small>
            </div>
          </div>
          <div v-else class="mt-4 flex items-center gap-3 text-[#8a94ad]">
            <i
              class="grid size-8 place-items-center rounded-full border border-dashed border-[#dcd6e7] bg-[#faf9fc]"
            >
              <i class="i-lucide-message-circle-off size-4" />
            </i>
            <span>
              <strong class="block text-[8px]"
                >Nenhuma atividade retornada</strong
              >
              <small class="mt-1 block text-[7px]"
                >O backend não expõe feed social para este perfil.</small
              >
            </span>
          </div>
        </article>

        <button
          class="mx-4 mt-4 grid h-11 w-[calc(100%_-_2rem)] place-items-center rounded-xl bg-gradient-to-r from-[#5a24c7] to-[#ef61bd] text-[9px] font-bold text-white disabled:cursor-not-allowed disabled:opacity-50"
          type="button"
          @click="openProfile"
        >
          Abrir perfil real
        </button>
      </section>

      <section
        v-else
        class="grid min-h-full place-items-center p-8 text-center text-[#8a94ad]"
      >
        <span>
          <i class="i-lucide-panel-right-open mx-auto size-8" />
          <strong class="mt-3 block text-[10px]">Selecione um corretor</strong>
          <small class="mt-1 block text-[8px]"
            >A visualização rápida será preenchida apenas com dados retornados
            pela API.</small
          >
          <small class="mt-4 block text-[7px] leading-3"
            >Missões para ganhar pontos indisponíveis até que um perfil real
            seja carregado.</small
          >
        </span>
      </section>
    </aside>

    <p
      v-if="error"
      class="fixed bottom-7 right-[526px] z-50 max-w-sm rounded-xl border border-rose-100 bg-rose-50 px-4 py-3 text-[9px] leading-4 text-rose-600"
    >
      {{ error }}
    </p>
  </main>
</template>
